import { Router, UrlTree } from '@angular/router';
import { DOCS_URL } from '@core/constants';

/**
 * Resolve a post-login redirect target safely. Never throws.
 * - Absolute URL whose origin is the docs site -> external navigation (allow-listed).
 * - Internal app path (single leading slash) -> client-side navigation.
 * - Empty / invalid / unsafe -> fall back to '/'.
 * Returns a Promise that resolves when navigation settles (true for the external
 * branch) so callers can keep a loading state until the page actually leaves.
 */
export function resolvePostLoginRedirect(
  router: Router,
  redirectUrl: string | null | undefined,
): Promise<boolean> {
  const target = redirectUrl?.trim();
  if (!target) {
    return router.navigateByUrl('/');
  }

  // Absolute URL? Only the allow-listed docs origin may leave the app.
  try {
    const url = new URL(target);
    if (url.origin === DOCS_URL) {
      globalThis.location.href = target;
      return Promise.resolve(true);
    }
    // Absolute but not allow-listed -> refuse (open-redirect guard).
    return router.navigateByUrl('/');
  } catch {
    // Not absolute -> treat as an internal path below.
  }

  return isSafeInternalPath(target) ? router.navigateByUrl(target) : router.navigateByUrl('/');
}

/**
 * UrlTree variant of {@link resolvePostLoginRedirect}, for use inside a route guard
 * that OWNS the post-login landing as a single navigation (the /auth-callback route).
 * Returning a UrlTree makes the redirect part of one router transition, so it cannot
 * race a separate navigateByUrl.
 * - Allow-listed DOCS origin -> side-effecting location.href, returns false to cancel
 *   the in-app navigation (a UrlTree can only express internal targets).
 * - Safe internal path -> UrlTree to that path.
 * - Empty / invalid / unsafe -> UrlTree to '/'.
 */
export function resolvePostLoginRedirectTree(
  router: Router,
  redirectUrl: string | null | undefined,
): UrlTree | boolean {
  const target = redirectUrl?.trim();
  if (!target) {
    return router.parseUrl('/');
  }

  // Absolute URL? Only the allow-listed docs origin may leave the app.
  try {
    const url = new URL(target);
    if (url.origin === DOCS_URL) {
      globalThis.location.href = target;
      return false;
    }
    // Absolute but not allow-listed -> refuse (open-redirect guard).
    return router.parseUrl('/');
  } catch {
    // Not absolute -> treat as an internal path below.
  }

  return isSafeInternalPath(target) ? router.parseUrl(target) : router.parseUrl('/');
}

/** Internal path = single leading slash, not protocol-relative, no backslash, no scheme. */
export function isSafeInternalPath(path: string): boolean {
  return (
    path.startsWith('/') && !path.startsWith('//') && !path.includes('\\') && !path.includes('://')
  );
}
